import BonusEligibilityDto from '@core/mappers/bonus/dtos/BonusEligibilityDto';
import {
    WelcomeBonusDto,
    WelcomeBonusResponseDto,
} from '@core/mappers/bonus/dtos/WelcomeBonusDto';
import { transformWelcomeBonus } from '@core/mappers/bonus/transformWelcomeBonus';
import { WelcomeOfferBonusType } from '@core/models/bonus/WelcomeOfferBonusType';
import { WelcomeBonusResponse } from '@models/bonus/WelcomeBonus';

import { getConfig } from '@superbet-group/web.lib.config';
import {
    createCTRestClient,
    RestClient,
} from '@superbet-group/web.lib.rest';
import { IResponse } from '@superbet-group/web.lib.utilities';
import { getClientSourceParam } from '@superbet-group/web.lib.vue-utilities';

import { defaultHttpTimeoutMs } from '@core/constants';
import { getCsrfToken } from '@core/services/authentication/helpers';
import { BonusResponse } from '@app/promotionHub/bonuses/types/BonusResponse';
import { default as BetlerBonusDto } from '@app/promotionHub/bonuses/types/betlerDto/BetlerBonusDto';
import BonusProvider from '@core/models/bonus/BonusProvider';
import { transformBonus } from '@app/promotionHub/bonuses/mappers/transformBonus';
import { BonusDto } from '@app/promotionHub/bonuses/types/BonusDto';
import { Bonus } from '@core/models/bonus/Bonus';
import { transformBonusEligibility } from '@core/mappers/bonus/transformBonusEligibility';
import { isBonusAvailableForDomain } from './helpers';

const endpoints = getConfig().services.playerAccountManagement;
const restClient: RestClient = createCTRestClient(
    getConfig().bonus.host,
    defaultHttpTimeoutMs,
    getClientSourceParam(),
    true,
    getCsrfToken,
);

export async function getPlayerBonuses(includeAwardingConditions: boolean): Promise<Bonus[]> {
    const response = await restClient.get<IResponse<BonusResponse | BetlerBonusDto[]>>(
        endpoints.getPlayerBonuses,
        {
            params: {
                includeAwardConditionFulfilment: includeAwardingConditions,
                lang: getConfig().appLocale.locale,
            },
        });

    let bonuses: BonusDto[] = getConfig().countrySpecific.user.bonus.bonusProvider === BonusProvider.Comtrade
        ? (response.data as BonusResponse).playerBonusHistory : response.data as BetlerBonusDto[];

    return bonuses.map((bonus: BonusDto) => isBonusAvailableForDomain(bonus)
        ? transformBonus[getConfig().countrySpecific.country](bonus)
        : null).filter((bonus) => !!bonus) as Bonus[];
}

export async function getAvailableBonuses(): Promise<WelcomeBonusResponse> {
    const response = await restClient.get<WelcomeBonusResponseDto>(
        endpoints.getAvailableBonuses,
        {
            params: {
                lang: getConfig().appLocale.locale,
            },
        },
    );
    return {
        welcomeOfferCampaignId: response.welcomeCampaignId ?? null,
        availableBonuses: response.availableBonuses?.map((bonus: WelcomeBonusDto) => transformWelcomeBonus[getConfig().countrySpecific.country](bonus)),
    };
}

export function setBonus(code: string): Promise<any> {
    if (code === WelcomeOfferBonusType.NoBonus) {
        return restClient.post(endpoints.bonusOptOut);
    } else {
        return restClient.post(
            endpoints.bonusOptIn,
            {
                type: code,
            },
        );
    }
}

export async function getBonusBetEligibility() {
    const response = await restClient.post<null, IResponse<BonusEligibilityDto>>(endpoints.getBetEligibility);
    return response.data.BetBonusEligibility.map((bbe) => transformBonusEligibility(bbe));
}
